import React, { useEffect, useMemo, useState } from "react";
import { fetchApi, getAssetUrl } from "../../config";

const STEP_TITLES = { 1: "Project type", 2: "Your details", 3: "Scope & brief" };
const TYPE_OPTIONS = [
    { value: "Custom Software", title: "Custom Software", desc: "Bespoke platform, tool or integration", iconTone: "ico--navy" },
    { value: "Website", title: "Website", desc: "Marketing site or web presence", iconTone: "ico--peach" },
    { value: "E-Commerce", title: "E-Commerce Store", desc: "Sell products or services online", iconTone: "ico--peach" },
    { value: "Digital Marketing", title: "Digital Marketing", desc: "SEO, PPC & social growth", iconTone: "ico--peach" },
];
const BUDGET_OPTIONS = ["< $5k", "$5k–$15k", "$15k–$50k", "$50k+"];
const TIMELINE_OPTIONS = ["ASAP", "1–3 months", "3–6 months", "Flexible"];

function initialState() {
    return { name: "", email: "", company: "", phone: "", details: "", budget: "", timeline: "" };
}

function renderTypeIcon(value) {
    switch (value) {
        case "Custom Software":
            return (
                <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" viewBox="0 0 24 24">
                    <polyline points="16 18 22 12 16 6"></polyline>
                    <polyline points="8 6 2 12 8 18"></polyline>
                </svg>
            );
        case "Website":
            return (
                <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" viewBox="0 0 24 24">
                    <rect height="14" rx="2" width="20" x="2" y="3"></rect>
                    <path d="M8 21h8"></path>
                    <path d="M12 17v4"></path>
                </svg>
            );
        case "E-Commerce":
            return (
                <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" viewBox="0 0 24 24">
                    <circle cx="8" cy="21" r="1"></circle>
                    <circle cx="19" cy="21" r="1"></circle>
                    <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
                </svg>
            );
        default:
            return (
                <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" viewBox="0 0 24 24">
                    <path d="m3 11 18-5v12L3 14v-3z"></path>
                    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path>
                </svg>
            );
    }
}

export default function ProjectModal({ open, onClose }) {
    const [step, setStep] = useState(1);
    const [projectType, setProjectType] = useState("");
    const [values, setValues] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState(null); // null | "sending" | "sent" | "error"

    useEffect(() => {
        if (!open) return;
        document.body.classList.add("nav-lock");
        return () => document.body.classList.remove("nav-lock");
    }, [open]);

    const handleTypeSelect = (value) => {
        setProjectType(value);
        setStep(2);
    };

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);

    const reset = () => {
        setStep(1);
        setProjectType("Custom Software");
        setValues(initialState());
        setErrors({});
        setStatus(null);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const updateValue = (name, value) => {
        setValues((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => {
            if (!prev[name]) return prev;
            const next = { ...prev };
            delete next[name];
            return next;
        });
    };

    const validateStep2 = () => {
        const nextErrors = {};
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRe = /^[+()\-\s\d]{7,}$/;

        if (!values.name.trim()) nextErrors.name = "Please enter your full name.";
        else if (values.name.trim().length < 2) nextErrors.name = "That name looks too short.";
        if (!values.email.trim()) nextErrors.email = "Please enter your email address.";
        else if (!emailRe.test(values.email.trim())) nextErrors.email = "Please enter a valid email address.";
        if (!values.company.trim()) nextErrors.company = "Please enter your company name.";
        else if (values.company.trim().length < 2) nextErrors.company = "That company name looks too short.";
        if (!values.phone.trim()) nextErrors.phone = "Please enter a phone number.";
        else if (!phoneRe.test(values.phone.trim())) nextErrors.phone = "Please enter a valid phone number.";

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const validateStep3 = () => {
        const nextErrors = {};
        if (!values.budget) nextErrors.budget = "Please pick a budget.";
        if (!values.timeline) nextErrors.timeline = "Please pick a timeline.";
        if (!values.details.trim() || values.details.trim().length < 10) nextErrors.details = "Tell us a little about your project (10+ characters).";
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const onNext = () => {
        if (step === 1) {
            setStep(2);
            return;
        }
        if (step === 2 && validateStep2()) {
            setStep(3);
        }
    };

    const onBack = () => setStep((prev) => Math.max(1, prev - 1));

    const onSubmit = async () => {
        if (!validateStep3()) return;
        setStatus("sending");
        try {
            const payload = new URLSearchParams();
            payload.append("project_type", projectType || selectedType.value);
            payload.append("name", values.name);
            payload.append("email", values.email);
            payload.append("company", values.company);
            payload.append("phone", values.phone);
            payload.append("budget", values.budget);
            payload.append("timeline", values.timeline);
            payload.append("details", values.details);

            const res = await fetchApi("submit_interest_project.php", {
                method: "POST",
                body: payload,
            });

            if (res && (res.result === true || res.success === true)) {
                setStatus("sent");
                setStep(4);
            } else {
                const msg = (res && (res.message || res.error)) || "Failed to submit brief.";
                setStatus("error");
                setErrors((prev) => ({ ...prev, _form: msg }));
            }
        } catch (err) {
            setStatus("error");
            setErrors((prev) => ({ ...prev, _form: err.message || String(err) }));
        }
    };

    const progressPercent = useMemo(() => `${(step / 3) * 100}%`, [step]);
    const selectedType = TYPE_OPTIONS.find((option) => option.value === projectType) || TYPE_OPTIONS[0];

    if (!open) return null;

    return (
        <div className="modal-overlay" onClick={(event) => (event.target === event.currentTarget ? handleClose() : null)}>
            <div aria-label="Start a project" aria-modal="true" className="modal" role="dialog">
                <div className="modal-head">
                    <div className="brand">
                        <span className="mark"><img alt="" src={getAssetUrl('kds-assets/img/kudosta-icon.png')} /></span>
                        <span className="title">Start a project</span>
                    </div>
                    <button aria-label="Close" className="modal-close" onClick={handleClose} type="button">×</button>
                </div>

                {step <= 3 && (
                    <div className="modal-progress">
                        <div className="row">
                            <span className="step">Step {step} of 3</span>
                            <span className="name">{STEP_TITLES[step]}</span>
                        </div>
                        <div className="track"><div className="fill" style={{ width: progressPercent }} /></div>
                    </div>
                )}

                <div className="modal-body">
                    {step === 1 && (
                        <div className="modal-step">
                            <h3>What can we build for you?</h3>
                            <p className="sub">Pick the closest fit — we'll tailor the details together.</p>
                            <div className="pick-grid">
                                {TYPE_OPTIONS.map((option) => (
                                    <button className={`pick${projectType === option.value ? " is-selected" : ""}`} key={option.value} onClick={() => handleTypeSelect(option.value)} type="button">
                                        <div className={`ico ${option.iconTone}`}>
                                            {renderTypeIcon(option.value)}
                                        </div>
                                        <div className="t">{option.title}</div>
                                        <div className="d">{option.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="modal-step">
                            <h3>Great — a {selectedType.value} project.</h3>
                            <p className="sub">Who should we get back to?</p>
                            <div className="form-grid-2">
                                <div className={`field ${errors.name ? "has-error" : ""}`}>
                                    <label htmlFor="modal-name">Full name</label>
                                    <input className="input" id="modal-name" name="name" onBlur={() => validateStep2()} onChange={(event) => updateValue("name", event.target.value)} placeholder="Jane Doe" value={values.name} />
                                    {errors.name && <div className="error-msg">{errors.name}</div>}
                                </div>
                                <div className={`field ${errors.email ? "has-error" : ""}`}>
                                    <label htmlFor="modal-email">Email</label>
                                    <input className="input" id="modal-email" name="email" onBlur={() => validateStep2()} onChange={(event) => updateValue("email", event.target.value)} placeholder="jane@company.com" value={values.email} />
                                    {errors.email && <div className="error-msg">{errors.email}</div>}
                                </div>
                                <div className={`field ${errors.company ? "has-error" : ""}`}>
                                    <label htmlFor="modal-company">Company</label>
                                    <input className="input" id="modal-company" name="company" onBlur={() => validateStep2()} onChange={(event) => updateValue("company", event.target.value)} placeholder="Company" value={values.company} />
                                    {errors.company && <div className="error-msg">{errors.company}</div>}
                                </div>
                                <div className={`field ${errors.phone ? "has-error" : ""}`}>
                                    <label htmlFor="modal-phone">Phone</label>
                                    <input className="input" id="modal-phone" name="phone" onBlur={() => validateStep2()} onChange={(event) => updateValue("phone", event.target.value)} placeholder="(123) 456 7890" value={values.phone} />
                                    {errors.phone && <div className="error-msg">{errors.phone}</div>}
                                </div>
                            </div>
                            <div className="modal-actions">
                                <button className="btn btn-ghost" onClick={onBack} type="button">← Back</button>
                                <button className="btn btn-primary btn--sm" onClick={onNext} type="button">Continue</button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="modal-step">
                            <h3>Tell us a little more.</h3>
                            <p className="sub">Rough details are fine — we'll refine on our call.</p>
                            <div className={`field ${errors.budget ? "has-error" : ""}`}>
                                <label>Budget range</label>
                                <div className="chip-row">
                                    {BUDGET_OPTIONS.map((option) => (
                                        <button className={`chip${values.budget === option ? " is-selected" : ""}`} key={option} onClick={() => updateValue("budget", option)} type="button">{option}</button>
                                    ))}
                                </div>
                                {errors.budget && <div className="chip-error">{errors.budget}</div>}
                            </div>
                            <div className={`field ${errors.timeline ? "has-error" : ""}`}>
                                <label>Timeline</label>
                                <div className="chip-row">
                                    {TIMELINE_OPTIONS.map((option) => (
                                        <button className={`chip${values.timeline === option ? " is-selected" : ""}`} key={option} onClick={() => updateValue("timeline", option)} type="button">{option}</button>
                                    ))}
                                </div>
                                {errors.timeline && <div className="chip-error">{errors.timeline}</div>}
                            </div>
                            <div className={`field ${errors.details ? "has-error" : ""}`}>
                                <label htmlFor="modal-details">Project details</label>
                                <textarea className="textarea" id="modal-details" name="details" onBlur={() => validateStep3()} onChange={(event) => updateValue("details", event.target.value)} placeholder="What are you trying to build or solve?" rows="3" value={values.details} />
                                {errors.details && <div className="error-msg">{errors.details}</div>}
                            </div>
                            {errors._form && <div className="form-note">{errors._form}</div>}
                            <div className="modal-actions">
                                <button className="btn btn-ghost" onClick={onBack} type="button">← Back</button>
                                <button className="btn btn-primary btn--sm" onClick={onSubmit} type="button" disabled={status === "sending"}>{status === "sending" ? "Sending…" : "Submit brief"}</button>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="modal-step">
                            <div className="modal-done">
                                <div className="modal-success-check">✓</div>
                                <h3>Brief received — thank you!</h3>
                                <p className="sub">We'll review your {selectedType.value} project and get back within one business day.</p>
                                <button className="btn btn-primary btn--sm" onClick={handleClose} type="button">Done</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
